"use client";

import {
  Button,
  Input,
  Textarea,
  Label,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  Badge,
  Separator,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  Checkbox,
  RadioGroup,
  RadioGroupItem,
  Switch,
  useTheme,
} from "@aio/platform-ui";
import { Moon, Sun, Monitor } from "lucide-react";

export default function PrimitivesPage() {
  const { theme, setTheme } = useTheme();
  return (
    <div className="space-y-10">
      <header className="flex flex-col items-start gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Primitives</h1>
          <p className="mt-1 text-fg-muted">All base components, theme-aware.</p>
        </div>
        <div className="flex gap-1 rounded-md border border-border p-1">
          <Button
            size="sm"
            variant={theme === "light" ? "secondary" : "ghost"}
            onClick={() => setTheme("light")}
          >
            <Sun className="size-4" /> Light
          </Button>
          <Button
            size="sm"
            variant={theme === "dark" ? "secondary" : "ghost"}
            onClick={() => setTheme("dark")}
          >
            <Moon className="size-4" /> Dark
          </Button>
          <Button
            size="sm"
            variant={theme === "system" ? "secondary" : "ghost"}
            onClick={() => setTheme("system")}
          >
            <Monitor className="size-4" /> System
          </Button>
        </div>
      </header>

      <section>
        <h2 className="text-lg font-semibold">Buttons</h2>
        <div className="mt-4 flex flex-wrap gap-3">
          <Button>Primary</Button>
          <Button variant="secondary">Secondary</Button>
          <Button variant="outline">Outline</Button>
          <Button variant="ghost">Ghost</Button>
          <Button variant="destructive">Destructive</Button>
          <Button loading>Loading</Button>
          <Button disabled>Disabled</Button>
          <Button size="sm">Small</Button>
          <Button size="lg">Large</Button>
        </div>
      </section>

      <Separator />

      <section>
        <h2 className="text-lg font-semibold">Form fields</h2>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="email">Email</Label>
            <Input id="email" placeholder="ime@firma.rs" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="invalid">Invalid input</Label>
            <Input id="invalid" aria-invalid="true" defaultValue="not-an-email" />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea id="notes" placeholder="Type something..." />
          </div>
          <div className="flex items-center gap-3">
            <Checkbox id="terms" defaultChecked />
            <Label htmlFor="terms">Accept terms</Label>
          </div>
          <div className="flex items-center gap-3">
            <Switch id="enabled" defaultChecked />
            <Label htmlFor="enabled">Enabled</Label>
          </div>
          <div className="space-y-2">
            <Label>Plan</Label>
            <RadioGroup defaultValue="trialing" className="flex gap-4">
              <div className="flex items-center gap-2">
                <RadioGroupItem value="trialing" id="r1" />
                <Label htmlFor="r1">Trialing</Label>
              </div>
              <div className="flex items-center gap-2">
                <RadioGroupItem value="active" id="r2" />
                <Label htmlFor="r2">Active</Label>
              </div>
            </RadioGroup>
          </div>
        </div>
      </section>

      <Separator />

      <section>
        <h2 className="text-lg font-semibold">Badges</h2>
        <div className="mt-4 flex flex-wrap gap-2">
          <Badge>Default</Badge>
          <Badge variant="success">Active</Badge>
          <Badge variant="warning">Trialing</Badge>
          <Badge variant="danger">Past due</Badge>
          <Badge variant="muted">Cancelled</Badge>
        </div>
      </section>

      <Separator />

      <section>
        <h2 className="text-lg font-semibold">Card</h2>
        <Card className="mt-4 max-w-md">
          <CardHeader>
            <CardTitle>Quarterly revenue</CardTitle>
            <CardDescription>Updated 2 minutes ago</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-semibold tabular-nums">EUR 428,512</p>
            <p className="mt-1 text-sm text-success">+12.4% vs last quarter</p>
          </CardContent>
        </Card>
      </section>

      <Separator />

      <section>
        <h2 className="text-lg font-semibold">Tabs</h2>
        <Tabs defaultValue="overview" className="mt-4">
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="history">History</TabsTrigger>
            <TabsTrigger value="settings">Settings</TabsTrigger>
          </TabsList>
          <TabsContent value="overview">
            <p className="text-sm text-fg-muted">Overview content goes here.</p>
          </TabsContent>
          <TabsContent value="history">
            <p className="text-sm text-fg-muted">History content goes here.</p>
          </TabsContent>
          <TabsContent value="settings">
            <p className="text-sm text-fg-muted">Settings content goes here.</p>
          </TabsContent>
        </Tabs>
      </section>

      <Separator />

      <section>
        <h2 className="text-lg font-semibold">Tooltip</h2>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="outline" className="mt-4">
              Hover me
            </Button>
          </TooltipTrigger>
          <TooltipContent>This is a tooltip</TooltipContent>
        </Tooltip>
      </section>
    </div>
  );
}
